import rp from "@reporting/rp-app-host";
import { ChatMessage } from "@reporting/rp-types/ai";

export type ChatUpdate = { idChat: number; messages: ChatMessage[] };

/** True while the last assistant message is still being generated by a background worker. */
export function isChatPending(messages?: ChatMessage[]) {
	const last = messages?.[messages.length - 1];
	return !!last && last.role === 'assistant' && (last.status === 'pending' || last.status === 'processing');
}

// Fields a poll can change on a message. `elapsed` / `heartbeat_age` are only set on a running
// answer (see ChatService::findMessages), so finished messages compare equal between polls.
const COMPARED_KEYS: (keyof ChatMessage)[] = [
	'status', 'content', 'trace', 'response', 'total_tokens', 'model', 'name', 'shared', 'login_name', 'elapsed', 'heartbeat_age',
];

function sameMessage(a: ChatMessage, b: ChatMessage) {
	for (const k of COMPARED_KEYS) if (a[k] !== b[k]) return false;
	return (a.attachments?.length ?? 0) === (b.attachments?.length ?? 0);
}

/**
 * Reuses the previous message objects for messages the poll did not change, so Lit does not
 * re-render finished messages (re-parse markdown, re-observe embedded views) on every tick —
 * only the answer in progress gets a new object.
 */
export function mergeMessages(prev: ChatMessage[] | undefined, next: ChatMessage[]): ChatMessage[] {
	if (!prev?.length) return next;
	const byIdz = new Map(prev.filter(m => m.idz != null).map(m => [m.idz, m]));
	return next.map(m => {
		const old = m.idz != null ? byIdz.get(m.idz) : undefined;
		return old && sameMessage(old, m) ? old : m;
	});
}

/**
 * Background poller for async (worker-generated) chat answers.
 *
 * Owned by the AIApplication (a long-lived singleton), so it keeps polling even when the
 * user switches to another module and the Chat components are detached from the DOM.
 * A chat that is on screen (watched by a connected Chat component) is polled fast, so the
 * worker's live progress (trace, heartbeat) shows up promptly; otherwise slowly. A hidden
 * browser tab is not polled at all: the loop pauses and catches up as soon as the tab is shown.
 *
 * Events (CustomEvent<ChatUpdate>):
 *  - `messages`: emitted every tick with the latest messages for a tracked chat
 *  - `done`: emitted once when a tracked chat's answer is resolved (done/error)
 */
export class ChatPoller extends EventTarget {

	/** poll interval while a tracked chat is on screen (live progress) */
	private static readonly POLL_INTERVAL_FAST = 2500;
	/** poll interval when no tracked chat is watched (the answer is awaited from another module) */
	private static readonly POLL_INTERVAL_SLOW = 5000;
	/** how often to ask the server to error-out long-stuck messages (crashed workers) */
	private static readonly SWEEP_INTERVAL = 120000;

	private readonly _tracked = new Set<number>();
	private readonly _watched = new Set<number>();
	private _timer?: number;
	private _ticking = false;
	private _lastSweep = Date.now();

	constructor() {
		super();
		rp.telemetry.addEventListener('visibility', () => this._onVisibility());
	}

	private get service() { return rp.ms('ai') }

	isTracking(idChat: number) { return this._tracked.has(idChat) }

	/** Start tracking a chat with a pending answer. No-op if already tracked. */
	track(idChat: number) {
		if (idChat > 0 && !this._tracked.has(idChat)) {
			this._tracked.add(idChat);
			this._reschedule();
		}
	}

	untrack(idChat: number) {
		this._tracked.delete(idChat);
	}

	/** A Chat component showing this chat is connected: poll it fast while its answer is pending. */
	watch(idChat: number) {
		if (idChat > 0 && !this._watched.has(idChat)) {
			this._watched.add(idChat);
			this._reschedule();
		}
	}

	unwatch(idChat: number) {
		this._watched.delete(idChat);
	}

	private get interval() {
		const live = [...this._tracked].some(id => this._watched.has(id));
		return live ? ChatPoller.POLL_INTERVAL_FAST : ChatPoller.POLL_INTERVAL_SLOW;
	}

	private _schedule() {
		if (!rp.telemetry.visible) return; // paused; _onVisibility re-arms the loop when the tab is shown
		this._timer = window.setTimeout(() => this._tick(), this.interval);
	}

	/**
	 * Re-plans the next tick with the current interval (a chat became tracked or watched);
	 * `immediate` runs it right away (the tab was shown again after a pause).
	 */
	private _reschedule(immediate = false) {
		if (!this._tracked.size || this._ticking) return;
		if (this._timer != null) window.clearTimeout(this._timer);
		if (immediate) this._tick(); // schedules the next tick itself when done
		else this._schedule();
	}

	private _onVisibility() {
		if (rp.telemetry.visible) {
			this._reschedule(true);
		} else if (this._timer != null) {
			window.clearTimeout(this._timer);
			this._timer = undefined;
		}
	}

	private async _tick() {
		this._timer = undefined;
		if (!this._tracked.size) return;
		this._ticking = true;
		try {
			// Safety net: ask the server to error-out long-stuck messages (crashed workers).
			if (Date.now() - this._lastSweep >= ChatPoller.SWEEP_INTERVAL) {
				this._lastSweep = Date.now();
				this.service.post('sweep', {}).catch(() => {});
			}

			for (const idChat of [...this._tracked]) {
				let messages: ChatMessage[] | undefined;
				try {
					messages = await this.service.get<ChatMessage[]>(`messages/${idChat}`);
				} catch { continue; }
				if (!this._tracked.has(idChat)) continue; // untracked while awaiting

				this.dispatchEvent(new CustomEvent<ChatUpdate>('messages', { detail: { idChat, messages } }));
				if (!isChatPending(messages)) {
					this._tracked.delete(idChat);
					this.dispatchEvent(new CustomEvent<ChatUpdate>('done', { detail: { idChat, messages } }));
				}
			}
		} finally {
			this._ticking = false;
		}

		if (this._tracked.size) this._schedule();
	}

}
